// Blocking "these were NOT created in Sage" dialog, shared by the CSV Bulk Import (PR #91) and the
// Accounts page's Sync Selected / Sync All. Error text is always run through explainSageError by
// the caller-supplied `error` -> here, so both screens say the same thing in the same words.
import { explainSageError } from '../lib/sageErrorMessage'

export interface SageFailureItem {
  key: string | number
  ref: string
  party?: string
  detail?: string
  error: string | undefined
}

interface Props {
  failCount: number
  total: number
  successCount: number
  noun: string
  items: SageFailureItem[]
  onExport?: () => void
  onClose: () => void
}

export default function SageFailureDialog({ failCount, total, successCount, noun, items, onExport, onClose }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="alertdialog" aria-modal="true" aria-labelledby="sage-failure-title">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id="sage-failure-title" className="text-lg font-semibold text-red-600 dark:text-red-400">
            {failCount} of {total} {noun}{failCount === 1 ? '' : 's'} were NOT created in Sage
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            The other {successCount} synced. These rows need attention before they will appear in Sage.
          </p>
        </div>
        <div className="overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
          {items.map(it => (
            <div key={it.key} className="px-6 py-3 text-sm">
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className="font-mono font-semibold text-gray-900 dark:text-white">{it.ref}</span>
                {it.party && <span className="text-gray-600 dark:text-gray-300">{it.party}</span>}
                {it.detail && <span className="text-gray-500 dark:text-gray-400">{it.detail}</span>}
              </div>
              <p className="text-red-600 dark:text-red-400 mt-1" title={it.error}>{explainSageError(it.error)}</p>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          {onExport && (
            <button onClick={onExport} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium">
              Export Failures CSV
            </button>
          )}
          <button onClick={onClose} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">
            I have seen these {failCount} failures
          </button>
        </div>
      </div>
    </div>
  )
}
